import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, MapPin, Phone, Mail, Clock, Building2, Headphones, FileText } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.pexels.com/photos/6285146/pexels-photo-6285146.jpeg?auto=compress&cs=tinysrgb&w=1920";
const OFFICE_IMG = "https://images.unsplash.com/photo-1730404691983-cc26f90b4f46?w=800&q=80";

export default function IletisimPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="iletisim-page">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C0C0C]/90 via-[#0C0C0C]/80 to-[#0C0C0C]/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#FF5A00] flex items-center justify-center">
                <Headphones size={22} className="text-white" />
              </div>
              <div>
                <p className="text-[#FF5A00] text-xs font-bold uppercase tracking-widest" style={{ fontFamily: "Outfit" }}>İletişim</p>
                <p className="text-white/50 text-xs">Bct Yapi Kurumsal</p>
              </div>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-tight mb-6" style={{ fontFamily: "Outfit" }}>
              Projeleriniz İçin<br /><span className="text-[#FF5A00]">Yanınızdayız</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-white/60 text-lg max-w-lg leading-relaxed">
              Uzman ekibimiz ile projenizi en ince detayına kadar planlayıp hayata geçiriyoruz. Hemen bizimle iletişime geçin.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Corporate Info Bar */}
      <div className="bg-[#0C0C0C] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/5">
            {[
              { icon: Phone, label: "Telefon", value: "0545 481 42 75", href: "tel:+905454814275" },
              { icon: Mail, label: "E-posta", value: "info@bctyapi.com", href: "mailto:info@bctyapi.com" },
              { icon: MapPin, label: "Merkez Ofis", value: "Batman, Türkiye" },
              { icon: Clock, label: "Çalışma", value: "Pzt-Cuma 09:00-18:00" },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="py-6 px-4 lg:px-6 text-center" data-testid={`info-bar-${i}`}>
                <item.icon size={18} className="text-[#FF5A00] mx-auto mb-2" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-1" style={{ fontFamily: "Outfit" }}>{item.label}</p>
                {item.href ? (
                  <a href={item.href} className="text-white text-sm font-medium hover:text-[#FF5A00] transition-colors">{item.value}</a>
                ) : (
                  <p className="text-white text-sm font-medium">{item.value}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left: Company Info + Image */}
          <div>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#0C0C0C] flex items-center justify-center">
                  <Building2 size={18} className="text-[#FF5A00]" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400" style={{ fontFamily: "Outfit" }}>Şirket Merkezi</p>
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold tracking-tighter text-[#0C0C0C] mb-4" style={{ fontFamily: "Outfit" }}>
                Bct Yapi İnşaat Mühendislik<br />
                <span className="text-[#FF5A00]">Sanayi ve Ticaret Ltd. Şti.</span>
              </h2>

              <div className="space-y-4 mt-8">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F9FAFB] border border-gray-100">
                  <div className="w-10 h-10 rounded-lg bg-[#FFF0E6] flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#FF5A00]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "Outfit" }}>Adres</p>
                    <p className="text-sm text-[#0C0C0C] font-medium leading-relaxed">Gültepe Mh. Eflatun Cd. ESF İşhanı<br />No:26/210 Batman, Batman</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F9FAFB] border border-gray-100">
                    <div className="w-9 h-9 rounded-lg bg-[#FFF0E6] flex items-center justify-center flex-shrink-0">
                      <Phone size={16} className="text-[#FF5A00]" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "Outfit" }}>Telefon</p>
                      <a href="tel:+905454814275" className="text-sm text-[#0C0C0C] font-medium hover:text-[#FF5A00] transition-colors">0545 481 42 75</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F9FAFB] border border-gray-100">
                    <div className="w-9 h-9 rounded-lg bg-[#FFF0E6] flex items-center justify-center flex-shrink-0">
                      <Mail size={16} className="text-[#FF5A00]" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "Outfit" }}>E-posta</p>
                      <a href="mailto:info@bctyapi.com" className="text-sm text-[#0C0C0C] font-medium hover:text-[#FF5A00] transition-colors">info@bctyapi.com</a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#F9FAFB] border border-gray-100">
                  <div className="w-10 h-10 rounded-lg bg-[#FFF0E6] flex items-center justify-center flex-shrink-0">
                    <Clock size={18} className="text-[#FF5A00]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1" style={{ fontFamily: "Outfit" }}>Çalışma Saatleri</p>
                    <p className="text-sm text-[#0C0C0C] font-medium">Pazartesi - Cuma: 09:00 - 18:00</p>
                    <p className="text-sm text-gray-500">Cumartesi: 09:00 - 14:00</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Office Image */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-8 rounded-2xl overflow-hidden shadow-xl">
              <img src={OFFICE_IMG} alt="BCT Yapı Ofis" className="w-full h-[240px] object-cover" loading="lazy" />
            </motion.div>
          </div>

          {/* Right: Contact Form */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-gray-100 sticky top-28">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#FF5A00] flex items-center justify-center">
                  <FileText size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#0C0C0C]" style={{ fontFamily: "Outfit" }}>Teklif Formu</h3>
                  <p className="text-xs text-gray-400">Projeniz hakkında bilgi alın</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>Ad Soyad</label>
                    <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="contact-input" placeholder="Adınız Soyadınız" data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>E-posta</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="contact-input" placeholder="ornek@email.com" data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>Telefon</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="contact-input" placeholder="+90 (___) ___ __ __" data-testid="contact-phone" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>Proje Detayları</label>
                  <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="contact-input resize-none" placeholder="Projenizin kapsamı, konumu ve beklentileriniz hakkında bilgi verin..." data-testid="contact-message" />
                </div>

                <div className="pt-2">
                  <button type="submit" disabled={loading} className="w-full bg-[#FF5A00] hover:bg-[#e64f00] text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm" style={{ fontFamily: "Outfit" }} data-testid="contact-submit">
                    <Send size={15} /> {loading ? "Gönderiliyor..." : "Teklif İste"}
                  </button>
                  {status === "success" && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-emerald-600 font-medium text-center mt-3">Mesajınız başarıyla gönderildi! En kısa sürede dönüş yapacağız.</motion.p>}
                  {status === "error" && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-red-500 font-medium text-center mt-3">Hata oluştu, lütfen tekrar deneyin.</motion.p>}
                </div>
              </form>

              <p className="text-[11px] text-gray-400 text-center mt-4 leading-relaxed">
                Formunuz gönderildikten sonra en geç 24 saat içinde uzman ekibimiz sizinle iletişime geçecektir.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
